import { version as PACKAGE_VERSION } from "../package.json";
import { AnalyticsService, CheckoutTimingTracker } from "./analytics";
import { supersedeModeFor } from "./payment-methods/bnpl-lifecycle";
import { createRadarSessionMintHandle, type RadarSessionMintHandle } from "./payment-methods/stripe-radar-session";
import { connectToTunnelXIframe } from "./penpal/connect-tunnel-x";
import {
  fetchSessionConfigWithRetry,
  SESSION_CONFIG_FETCH_TIMEOUT_MS,
  SESSION_CONFIG_MAX_ATTEMPTS,
  SessionConfigTimeoutError,
} from "./session-config-fetch";
import type { ExternalFuncsMapByPm, PaymentKit as PaymentKitType } from "./types";
import { getUrlsForEnvironment } from "./types";
import {
  AIRWALLEX_FRAUD_SCRIPT_ID,
  adoptFraudScript,
  clearAirwallexScriptExpected,
  createCheckoutIFrame,
  fraudScriptReady,
  markAirwallexScriptExpected,
  markFraudScriptLoaded,
  releaseFraudScript,
} from "./utils";
import { getOrCreateCheckoutRequestId } from "./utils/checkout-request-id";
import { loadScript } from "./utils/load-script";

/**
 * Map fraud script URL to the script ID that the processor's script expects.
 * Airwallex's fingerprint script looks for `id="airwallex-fraud-api"` via getElementById.
 * Other processors may have different ID requirements.
 */
function getFraudScriptId(url: string): string {
  // Airwallex expects exactly "airwallex-fraud-api" as the script ID
  if (url.includes("airwallex.com")) {
    return AIRWALLEX_FRAUD_SCRIPT_ID;
  }
  // Fallback for other processors: use a hash-based ID
  const urlObj = new URL(url);
  const hash = btoa(urlObj.origin + urlObj.pathname)
    .replace(/[^a-zA-Z0-9]/g, "")
    .slice(0, 12);
  return `pk-fraud-script-${hash}`;
}

type PaymentKitReturnType = ReturnType<PaymentKitType>;

/**
 * Logs a swallowed cleanup/teardown failure and reports it to analytics — teardown failures
 * are caught (rather than thrown) so one step's failure can't abort the rest of cleanup, but
 * a swallowed error is otherwise invisible: it lands in the *merchant's* browser console,
 * which we never read. Without this, PaymentKit has no way to learn a teardown guard fired
 * in the field short of a merchant reporting it manually (as happened for PAY-1741).
 */
const reportCleanupFailure = (stage: string, err: unknown) => {
  console.warn(`[PaymentKit] cleanup step failed (${stage}):`, err);
  AnalyticsService.capture("paymentkit_cleanup_teardown_failed", {
    stage,
    error: err instanceof Error ? err.message : String(err),
  });
};

const createTunnelXConnection = (baseUrl: string, apiBaseUrl: string, token: string) => {
  const iframe = createCheckoutIFrame("tunnel-x", baseUrl, {
    checkout_token: token,
    api_base_url: apiBaseUrl,
  });
  // Tunnel-X is a hidden communication iframe, not a visible UI element
  Object.assign(iframe.style, { width: "0", height: "0", position: "absolute", visibility: "hidden" });
  document.body.appendChild(iframe);

  const connection = connectToTunnelXIframe(iframe, {});

  let destroyed = false;
  const unmount = () => {
    if (destroyed) return;
    destroyed = true;
    connection.destroy();
    iframe.remove();
  };

  return { unmount, connection };
};

const PaymentKit: PaymentKitType = ({
  environment = "production",
  // Renamed locally so it doesn't shadow the derived `apiBaseUrl` below. A merchant passes a single
  // custom https origin to route all FIRST-PARTY traffic at their own domain (custom-domain cure); older PKJS
  // versions ignore this key, so the same init object is safe for an old↔new A/B.
  apiBaseUrl: apiBaseUrlOverride,
  secureToken,
  paymentMethods,
  cardTokenizationMode,
  vgsVaultId,
  vgsEnvironment,
  stripeRadarPublishableKey,
  processorIds,
  requestShippingAddress,
  __enableAnalyticsTestMode,
}) => {
  type PaymentMethods = typeof paymentMethods;

  // Resolve URLs — from the merchant's custom-domain override when given, else from `environment`.
  // This is the ONE derivation point; both URLs thread through paymentKitStates to every first-party
  // network host (config fetch, /ingest, the tunnel-x + card iframes, cardCheckout/CSI/wallet/BNPL/
  // SEPA/Link). Third-party hosts (PostHog's ui_host, Stripe/Adyen/VGS/Apple/Google CDNs) are not
  // rewritten.
  const { baseUrl, apiBaseUrl } = getUrlsForEnvironment(environment, apiBaseUrlOverride);
  // Whether a custom-domain override is actually routing traffic (a valid override that was applied,
  // not merely passed): true only when the resolved host differs from the environment default. Lets
  // analytics tell a bad custom domain apart from the original app.paymentkit.com block.
  const apiBaseUrlOverridden = apiBaseUrl !== getUrlsForEnvironment(environment).apiBaseUrl;

  // Log version for debugging
  console.log(`[PaymentKit] v${PACKAGE_VERSION} initialized (env: ${environment})`);

  // Generate checkout request ID for correlating all API calls and analytics events
  // This is idempotent - same ID is reused across multiple PaymentKit instantiations
  const checkoutRequestId = getOrCreateCheckoutRequestId(environment);
  console.log(`[PaymentKit] checkout_request_id: ${checkoutRequestId}`);

  // Initialize analytics and identify with secure token for correlation
  AnalyticsService.init(apiBaseUrl, __enableAnalyticsTestMode);
  AnalyticsService.identify(secureToken);

  // Create timing tracker for this checkout session
  const timingTracker = new CheckoutTimingTracker(secureToken, checkoutRequestId);

  // Track page ready immediately
  timingTracker.trackPageReady();

  const { connection: tunnelXConnection, unmount: unmountTunnelX } = createTunnelXConnection(
    baseUrl,
    apiBaseUrl,
    secureToken,
  );

  const paymentKitStates = {
    baseUrl,
    apiBaseUrl,
    secureToken,
    environment,
    tunnelXConnection,
    timingTracker,
    checkoutRequestId,
    clientVersion: PACKAGE_VERSION,
    cardTokenizationMode,
    vgsVaultId,
    vgsEnvironment,
    stripeRadarPublishableKey,
    processorIds,
    requestShippingAddress,
    _sessionConfigReady: undefined as Promise<void> | undefined,
    // Carries a terminal session-config failure as STATE so `_sessionConfigReady` always RESOLVES
    // and never rejects (consumers read this instead of hand-guarding a rejection).
    sessionConfigError: undefined as SessionConfigTimeoutError | undefined,
    _initialSessionConfig: undefined as Promise<unknown> | undefined,
    _radarSessionMint: undefined as RadarSessionMintHandle | undefined,
    _fraudScriptIds: [] as string[],
    _fraudScriptsReady: undefined as Promise<void> | undefined,
    _onPaymentStarting: undefined as ((paymentMethodName: string) => void) | undefined,
  };
  // Settles once the fraud scripts have loaded or failed, or once we know there
  // are none (session fetched, no URLs) or cannot know (fetch failed). Created
  // synchronously so a card submit can wait on it, bounded, before building the
  // body that carries the device id. Wallets never wait on it (PAY-1538: nothing
  // may be awaited between the tap and the sheet). Never rejects.
  let settleFraudScripts: () => void = () => {};
  // Set by cleanup(). The session fetch below can resolve after the instance is
  // torn down (React StrictMode, an SPA re-render); injecting then would leave a
  // script and a reference no cleanup() will ever release.
  let destroyed = false;
  let expectedAirwallexScript = false;
  paymentKitStates._fraudScriptsReady = new Promise<void>((resolve) => {
    settleFraudScripts = resolve;
  });

  // Inject the processors' fraud fingerprinting scripts (Airwallex's
  // device-fingerprint script, keyed by this checkout's request id). Runs on BOTH
  // init branches below: until Sept 2026 it lived only inside the auto-detect
  // branch, and every integration that passes cardTokenizationMode explicitly
  // (our own hosted checkout and billing portal do) never loaded the script, so
  // the airwallexDeviceId they sent pointed at a fingerprint session Airwallex
  // never received.
  // biome-ignore lint/suspicious/noExplicitAny: session API payload
  const injectFraudScripts = (session: any): void => {
    if (destroyed || !session?.fraud_script_urls?.length) {
      settleFraudScripts();
      return;
    }
    if (session.fraud_script_urls.some((url: string) => getFraudScriptId(url) === AIRWALLEX_FRAUD_SCRIPT_ID)) {
      markAirwallexScriptExpected();
      expectedAirwallexScript = true;
    }
    const loadPromises: Promise<void>[] = [];

    session.fraud_script_urls.forEach((url: string) => {
      const scriptId = getFraudScriptId(url);

      const alreadyPresent = fraudScriptReady(scriptId);
      if (alreadyPresent) {
        // Another live instance injected it: share its load promise and record
        // (never the element's presence) and count ourselves as a user of it.
        console.log(`[PaymentKit] Fraud script already present: ${scriptId}`);
        adoptFraudScript(scriptId, alreadyPresent);
        paymentKitStates._fraudScriptIds.push(scriptId);
        loadPromises.push(alreadyPresent);
        return;
      }
      if (document.getElementById(scriptId)) {
        // Put there by something other than a live PaymentKit instance (a
        // torn-down one, or the merchant page): we know nothing about it.
        console.log(`[PaymentKit] Fraud script element present without a load record: ${scriptId}`);
        loadPromises.push(Promise.resolve());
        return;
      }

      const script = document.createElement("script");
      script.type = "text/javascript";
      script.async = true;
      script.id = scriptId;
      script.src = url;
      script.dataset.orderSessionId = checkoutRequestId;

      // Resolves on error too: an optional fraud script never blocks payment.
      const loadPromise = loadScript(script, (loaded) => {
        if (loaded) {
          console.log(`[PaymentKit] Fraud script loaded: ${scriptId}`);
        } else {
          console.error(
            `[PaymentKit] Failed to load fraud prevention script. If using Content-Security-Policy, add to script-src: ${new URL(url).origin}`,
          );
        }
        markFraudScriptLoaded(scriptId, loaded);
      }).then(() => undefined);

      adoptFraudScript(scriptId, loadPromise);
      loadPromises.push(loadPromise);
      paymentKitStates._fraudScriptIds.push(scriptId);
      console.log(
        `[PaymentKit] Fraud script injected: id=${scriptId}, data-order-session-id=${checkoutRequestId}, src=${url}`,
      );
    });

    Promise.all(loadPromises).then(() => {
      console.log(`[PaymentKit] All ${loadPromises.length} fraud fingerprinting script(s) ready`);
      settleFraudScripts();
    });
  };

  // One Radar mint per init (PAY-2001), started as soon as the key is known. An
  // explicit key starts it at once; else it starts when the session config arrives,
  // however late (the submit bounds its own wait). `sessionReady` never rejects.
  const startRadarSessionMint = (sessionReady: Promise<unknown>): RadarSessionMintHandle =>
    createRadarSessionMintHandle(
      stripeRadarPublishableKey
        ? Promise.resolve(stripeRadarPublishableKey)
        : sessionReady.then(() => paymentKitStates.stripeRadarPublishableKey),
      () => destroyed,
    );

  // Auto-detect VGS mode from session API if not explicitly provided.
  // The promise resolves before any card mount or submit needs the mode.
  // Correlate the session fetch to the client so the backend/LB can log it (a `[CHECKOUT]
  // config_served` line keyed by request_id). Passed as a QUERY PARAM, not a custom request
  // header: this GET is cross-origin (app.paymentkit.com from the merchant page), and a custom
  // header (e.g. the `x-request-id` that `withRequestId` sets for the tunnel SDK calls) would
  // trigger a CORS preflight — an extra round-trip that can itself hang and needs backend
  // allow-listing. A query param rides the simple GET with no preflight and is logged anyway.
  // Both branches below fetch this URL once, with the same bounded retry.
  const sessionConfigUrl = `${apiBaseUrl}/api/checkout-sessions/token/${secureToken}?request_id=${encodeURIComponent(
    checkoutRequestId,
  )}`;
  if (cardTokenizationMode === undefined) {
    console.log(
      `[PaymentKit] cardTokenizationMode not provided — auto-detecting from session API: ${sessionConfigUrl}`,
    );
    // Fail-loud terminal handler (no direct-mode fallback — a VGS session 422s a raw PAN, so direct
    // was a dead end). Called exactly once on EVERY terminal-failure path (exhaustion and any
    // non-retryable terminal error). It only records/signals; the failure is carried as
    // `sessionConfigError` STATE so `_sessionConfigReady` resolves rather than rejects, and the
    // consumers read that state (submit re-throws it to onError; mounts skip; the saved-card reads
    // return `{ errors }`). The `console.error` is a stable, greppable prefix with structured
    // fields that is intentionally meant for the MERCHANT's own client-side error collection (tools
    // like subdelete collect console errors) — unlike reportCleanupFailure's note about a console
    // WE never read, this line is meant to be collected. The analytics event stays for OUR counting;
    // settleFraudScripts keeps a submit's bounded fingerprint wait from adding a pointless delay.
    const reportSessionConfigFailure = (attempts: number) => {
      // Do NOT log the secureToken or the token-bearing config URL (it contains /token/<secureToken>)
      // — it is a bearer token and must stay out of logs/error collectors (matches the backend
      // PAY-1934 omission). checkoutRequestId is the correlation key.
      console.error("[PaymentKit] session_config_fetch_failed", {
        checkoutRequestId,
        attempts,
        timeoutMs: SESSION_CONFIG_FETCH_TIMEOUT_MS,
      });
      AnalyticsService.capture("session_config_fetch_failed", {
        attempts,
        timeout_ms: SESSION_CONFIG_FETCH_TIMEOUT_MS,
        // Distinguish a bad custom domain (override in use) from the original app.paymentkit.com block.
        api_base_url_overridden: apiBaseUrlOverridden,
      });
      settleFraudScripts();
    };

    // `_sessionConfigReady` NEVER rejects — by construction. The whole body is wrapped in one
    // try/catch whose catch does not rethrow, so the promise can only resolve; a terminal failure
    // is recorded as `sessionConfigError` state instead. This guarantees ONLY that no consumer can
    // trigger an unhandled rejection — it does NOT by itself make a consumer correct: a consumer
    // that awaits `_sessionConfigReady` but forgets to check `sessionConfigError` still falls through
    // with an undefined mode. It trades a hard-to-spot rejection-handling requirement for a visible
    // state check each consumer must still perform.
    const sessionConfigFetch = fetchSessionConfigWithRetry(sessionConfigUrl);
    // Wallet auto-prepare reads this same body instead of fetching the endpoint again (PAY-2447).
    // A failed fetch gives them `null`, and they fetch on their own as before.
    paymentKitStates._initialSessionConfig = sessionConfigFetch.catch(() => null);
    paymentKitStates._sessionConfigReady = (async () => {
      try {
        // biome-ignore lint/suspicious/noExplicitAny: session API payload
        const session: any = await sessionConfigFetch;

        // Success: honor whatever mode the session returned, exactly as before (a legitimate
        // `direct` from the server is honored here and never retried).
        if (session && typeof session === "object" && typeof session.card_tokenization_mode === "string") {
          paymentKitStates.cardTokenizationMode = session.card_tokenization_mode || "direct";
          console.log(
            `[PaymentKit] Session config detected — cardTokenizationMode: ${paymentKitStates.cardTokenizationMode}`,
          );

          // Validate VGS config from API before trusting it
          const vaultId = session.vgs_vault_id;
          const vgsEnv = session.vgs_environment;
          if (vaultId && /^tnt[a-z0-9]+$/.test(vaultId) && (vgsEnv === "sandbox" || vgsEnv === "live")) {
            paymentKitStates.vgsVaultId = vaultId;
            paymentKitStates.vgsEnvironment = vgsEnv;
            console.log(`[PaymentKit] VGS config valid — vaultId: ${vaultId}, environment: ${vgsEnv}`);
          } else if (paymentKitStates.cardTokenizationMode === "vgs") {
            console.warn(
              `[PaymentKit] Invalid VGS config from session API (vaultId: ${vaultId}, env: ${vgsEnv}), falling back to direct mode`,
            );
            paymentKitStates.cardTokenizationMode = "direct";
          }

          // Stripe Radar publishable key (PAY-1726): used at submit time to mint a
          // Radar session carrying client IP/device for Visa CE 3.0 / Mastercard FPT
          // matching. An explicitly-passed key always wins over the session API.
          if (!paymentKitStates.stripeRadarPublishableKey && typeof session.stripe_radar_publishable_key === "string") {
            paymentKitStates.stripeRadarPublishableKey = session.stripe_radar_publishable_key;
            console.log("[PaymentKit] Stripe Radar publishable key detected");
          }

          // A malformed fraud-script URL must NOT turn a SUCCESSFUL config into a failure, and must
          // not escape to reject the promise — isolate it and settle the fraud waiters.
          try {
            injectFraudScripts(session);
          } catch (fraudErr) {
            console.warn("[PaymentKit] Fraud script injection failed:", fraudErr);
            settleFraudScripts();
          }
          return; // success: mode set, no sessionConfigError
        }

        // A 2xx with no usable card_tokenization_mode is terminal: there is no direct-mode fallback,
        // and retrying the same endpoint won't change the body.
        throw new SessionConfigTimeoutError("session config response missing card_tokenization_mode", 1);
      } catch (err) {
        // The ONLY settle-on-failure path. Record the failure as STATE and RESOLVE (never reject).
        // This catch never rethrows and cannot itself throw, which is what makes the never-reject
        // property hold by construction.
        const error =
          err instanceof SessionConfigTimeoutError
            ? err
            : new SessionConfigTimeoutError(String(err), SESSION_CONFIG_MAX_ATTEMPTS);
        paymentKitStates.sessionConfigError = error;
        reportSessionConfigFailure(error.attempts);
      }
    })();
    // `_sessionConfigReady` never rejects, so Radar's keyReady (derived from it) can't reject
    // either (its invariant holds). A failed config just leaves no Radar key → the mint settles as
    // no_key, exactly as before.
    paymentKitStates._radarSessionMint = startRadarSessionMint(paymentKitStates._sessionConfigReady);
  } else {
    console.log(`[PaymentKit] cardTokenizationMode explicitly set: ${cardTokenizationMode}`);

    // An integrator who passes cardTokenizationMode explicitly never enters the auto-detect
    // branch above, so this branch makes its own (single) session fetch. The body carries the
    // Radar key (CE 3.0/FPT matching, PAY-1726), the fraud script URLs, and the config wallet
    // auto-prepare reuses (PAY-2447). It uses fetchSessionConfigWithRetry's 8s per-attempt bound
    // (2 attempts, ~20s worst case), so the wallets waiting on it never hang. Trade-off: a response
    // slower than 8s per attempt means no Radar key / fraud scripts for this init, and the wallets
    // fall back to their own fetch. A terminal failure lands in the `.catch` below.
    const sessionBody = fetchSessionConfigWithRetry(sessionConfigUrl);
    // Wallet auto-prepare reads this same body (PAY-2447); `null` makes them fetch on their own.
    paymentKitStates._initialSessionConfig = sessionBody.catch(() => null);
    const sessionFetch = sessionBody
      .then((session) => {
        if (!stripeRadarPublishableKey && typeof session?.stripe_radar_publishable_key === "string") {
          paymentKitStates.stripeRadarPublishableKey = session.stripe_radar_publishable_key;
        }
        injectFraudScripts(session);
      })
      .catch((err) => {
        console.warn("[PaymentKit] Session config fetch failed — continuing without Radar key / fraud scripts:", err);
        settleFraudScripts();
      });
    paymentKitStates._radarSessionMint = startRadarSessionMint(sessionFetch);
  }

  // The checkout never locks while a BNPL popup is open. Starting any other payment ends the
  // pending BNPL submit: the shopper left that popup. Runs synchronously, before the target's own
  // wallet sheet opens, so the click's user activation is kept. A BNPL target calls it itself, once
  // its options are valid, its popup is open and the merchant's beforeConfirm passed. Set before
  // the instances are built so every method sees it on the shared states.
  paymentKitStates._onPaymentStarting = (paymentMethodName) => {
    const target = pmInstances.find(({ name }) => name === paymentMethodName);
    const mode = supersedeModeFor(Boolean(target?.internalFuncs.supersede));
    for (const pm of pmInstances) {
      if (pm !== target) pm.internalFuncs.supersede?.(mode);
    }
  };

  const pmInstances = paymentMethods.map((paymentMethod) => paymentMethod(paymentKitStates));

  const externalFuncsMapByPm: ExternalFuncsMapByPm<PaymentMethods> = pmInstances.reduce(
    (acc, { name, externalFuncs }) => {
      // @ts-expect-error - typecase this better in future
      acc[name] = externalFuncs;
      return acc;
    },
    {} as ExternalFuncsMapByPm<PaymentMethods>,
  );

  const submit: PaymentKitReturnType["submit"] = ({
    paymentMethod: paymentMethodName,
    fields,
    options,
    onSuccess,
    onError,
  }) => {
    const paymentMethod = pmInstances.find(({ name }) => name === paymentMethodName);
    if (!paymentMethod) {
      onError({ root: "payment_method_not_found" });
      return;
    }
    // A BNPL target ends the others itself, so an invalid, blocked or gate-rejected submit leaves
    // them pending.
    if (!paymentMethod.internalFuncs.supersede) paymentKitStates._onPaymentStarting?.(String(paymentMethodName));
    paymentMethod.internalFuncs
      .submitPayment(fields, options)
      .then(({ data, errors, tornDown, superseded, joined }) => {
        // A torn-down BNPL submit settles silently: the merchant already left this checkout.
        // A superseded BNPL submit settles silently: the shopper started another payment.
        // A joined BNPL submit shares the first call's result: only the first call reports it.
        if (tornDown || superseded || joined) return;
        errors ? onError(errors) : onSuccess(data);
      })
      .catch((e) => {
        console.error("PaymentKit:submit:catch", e);

        // Try to extract error message from response
        if (e?.response?.data) {
          onError(e.response.data);
        } else if (e?.message) {
          onError({ root: e.message });
        } else {
          onError({ root: "unknown_error" });
        }
      });
  };

  const cleanup = () => {
    destroyed = true;
    if (expectedAirwallexScript) clearAirwallexScriptExpected();
    // Release this instance's hold on the fraud fingerprinting scripts. The
    // element (and its load record, which gates the device id) only goes once
    // no live instance on the page relies on it.
    for (const scriptId of paymentKitStates._fraudScriptIds) {
      if (releaseFraudScript(scriptId)) {
        document.getElementById(scriptId)?.remove();
      }
    }
    paymentKitStates._fraudScriptIds = [];

    // Clean up all payment method instances. Each teardown is isolated: a payment
    // method's cleanup may touch merchant-owned DOM the merchant has already removed
    // (e.g. a re-render mid-checkout) and throw. One method's teardown failure must
    // not skip the remaining methods' cleanup or the Tunnel-X unmount below.
    for (const pm of pmInstances) {
      if (pm.internalFuncs.cleanup) {
        try {
          pm.internalFuncs.cleanup();
        } catch (err) {
          reportCleanupFailure(`payment_method:${pm.name}`, err);
        }
      }
    }
    try {
      unmountTunnelX();
    } catch (err) {
      reportCleanupFailure("tunnel_x_unmount", err);
    }
  };

  return {
    submit,
    cleanup,
    ...externalFuncsMapByPm,
  };
};

export type {
  BeforeConfirmDecision,
  BeforeConfirmHook,
  BeforeConfirmOptions,
} from "./payment-methods/before-confirm";
export { DEFAULT_BEFORE_CONFIRM_TIMEOUT_MS } from "./payment-methods/before-confirm";
// Saved-card one-click (CIT_SAVED_CARD_ONE_CLICK §14): the published shapes returned by
// paymentKit.card.listSavedCards() / .getSavedCardCvcReprompt().
export type { ListSavedCardsResult, SavedCard, SavedCardCvcRepromptResult } from "./payment-methods/card";
// Public for a merchant's `onError`: because the submit wrapper flattens throws to
// `onError({ root: error.message })`, match the stable `session_config_fetch_failed` marker that the
// message begins with (`error.root?.startsWith("session_config_fetch_failed")`), NOT `instanceof`
// (the typed instance does not survive the wrapper). The retry mechanism (and parseRetryAfterMs)
// stays internal to ./session-config-fetch.
export { SessionConfigTimeoutError } from "./session-config-fetch";
export type { PaymentKitEnvironment, PaymentKitFields } from "./types";
export type { FraudMetadataWire } from "./utils";

export default PaymentKit;
